import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { prefetchBlogsList } from '../pages/Blog';


let gsapModule: typeof import('gsap') | null = null;

interface MobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

function MobileSidebar({ isOpen, onClose }: MobileSidebarProps) {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const menuItemsRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const menuItems = [
    { path: '/', label: 'Home' },
    { path: '/projects', label: 'Projects' },
    { path: '/blog', label: 'Blog' },
    { path: '/about', label: 'About' },
    { path: '/resume.pdf', label: 'Resume', external: true },
  ];

  useEffect(() => {
    const sidebar = sidebarRef.current;
    const overlay = overlayRef.current;
    const items = menuItemsRef.current?.children;

    if (!sidebar || !overlay || !items) return;

    // Load GSAP on first use (client-side only)
    const loadAndAnimate = async () => {
      if (!gsapModule) {
        gsapModule = await import('gsap');
      }
      const { gsap } = gsapModule;

      const ctx = gsap.context(() => {
        if (isOpen) {
          // Opening animation
          gsap.set(sidebar, { x: '100%' });
          gsap.set(overlay, { opacity: 0, display: 'block' });
          gsap.set(items, { x: 50, opacity: 0 });

          const tl = gsap.timeline();

          tl.to(overlay, {
            opacity: 1,
            duration: 0.3,
            ease: 'power2.out',
          })
          .to(sidebar, {
            x: '0%',
            duration: 0.5,
            ease: 'power3.out',
          }, '-=0.2')
          .to(items, {
            x: 0,
            opacity: 1,
            duration: 0.4,
            stagger: 0.1,
            ease: 'power3.out',
          }, '-=0.3');

        } else {
          // Closing animation
          const tl = gsap.timeline();

          tl.to(items, {
            x: 50,
            opacity: 0,
            duration: 0.2,
            stagger: 0.05,
            ease: 'power2.in',
          })
          .to(sidebar, {
            x: '100%',
            duration: 0.4,
            ease: 'power3.in',
          }, '-=0.1')
          .to(overlay, {
            opacity: 0,
            duration: 0.3,
            ease: 'power2.in',
            onComplete: () => {
              gsap.set(overlay, { display: 'none' });
            },
          }, '-=0.3');
        }
      });

      return ctx;
    };

    let ctx: any;
    loadAndAnimate().then(c => { ctx = c; });

    return () => {
      if (ctx) ctx.revert();
    };
  }, [isOpen]);

  // Close on route change
  useEffect(() => {
    onClose();
  }, [location.pathname]);

  return (
    <>
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 hidden md:hidden"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className="fixed top-0 right-0 h-full w-[75%] max-w-[300px] bg-primary border-l border-secondary z-50 md:hidden"
        style={{ transform: 'translateX(100%)' }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-2 hover:bg-secondary/50 rounded-full transition-colors"
          aria-label="Close menu"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Menu items */}
        <nav ref={menuItemsRef} className="flex flex-col pt-24 px-8">
          {menuItems.map((item, index) => (
            item.external ? (
              <a
                key={item.path}
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                className="group py-4 border-b border-secondary/50"
              >
                <span className="text-3xl font-light tracking-wide group-hover:text-muted transition-colors">
                  {item.label}
                </span>
                <div className="h-[2px] w-0 bg-accent group-hover:w-full transition-all duration-300 mt-1" />
              </a>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                onMouseEnter={item.label === 'Blog' ? prefetchBlogsList : undefined}
                className={`group py-4 border-b border-secondary/50 ${
                  location.pathname === item.path ? 'text-accent' : ''
                }`}
              >
                <span className="text-3xl font-light tracking-wide group-hover:text-muted transition-colors">
                  {item.label}
                </span>
                <div 
                  className={`h-[2px] bg-accent transition-all duration-300 mt-1 ${
                    location.pathname === item.path ? 'w-12' : 'w-0 group-hover:w-full'
                  }`} 
                />
              </Link>
            )
          ))}
        </nav>

        {/* Bottom section */}
        <div className="absolute bottom-8 left-8 right-8">
          <div className="flex gap-4 mb-4">
            <a 
              href="https://github.com/Dsmalldara" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-3 border border-secondary hover:bg-secondary/50 rounded-full transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
            </a>
            <a 
              href="https://linkedin.com/in/dada-darasimi" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-3 border border-secondary hover:bg-secondary/50 rounded-full transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            <a 
              href="mailto:topedarasimi5@gmail.com"
              className="p-3 border border-secondary hover:bg-secondary/50 rounded-full transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </a>
          </div>
          <p className="text-xs text-muted">
            © 2025 Darasimi
          </p>
        </div>
      </div>
    </>
  );
}

export default MobileSidebar;
